/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  Input,
  Radio,
  RadioOption,
  Text,
} from 'src/@vymo/ui/atoms';
import {
  DndContext,
  DragEndEvent,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import classnames from 'classnames';
import { ReactComponent as DraggableIcon } from 'src/assets/icons/draggable.svg';
import { ReactComponent as ResetIcon } from 'src/assets/icons/reset.svg';
import { ReactComponent as SearchIcon } from 'src/assets/icons/search.svg';
import { Keys, locale } from 'src/i18n';
import styles from './index.module.scss';

function arrayMove<T>(array: T[], from: number, to: number) {
  const newArray = [...array];
  const [item] = newArray.splice(from, 1);
  newArray.splice(to, 0, item);
  return newArray;
}

function DraggableColumnRow({ column }: { column: any }) {
  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
    transform,
    isDragging,
  } = useDraggable({ id: column.id });

  const { setNodeRef: setDroppableRef } = useDroppable({ id: column.id });

  const setNodeRef = (node: HTMLElement | null) => {
    setDraggableRef(node);
    setDroppableRef(node);
  };

  const style = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      className={styles.columnConfigurator__input__checkbox__columns}
      style={style}
    >
      <Checkbox
        value={column.getIsVisible()}
        label={column?.columnDef?.header || ''}
        onChange={(checked) => column.toggleVisibility(checked)}
      />
      <span {...listeners} {...attributes} style={{ cursor: 'grab' }}>
        <DraggableIcon />
      </span>
    </div>
  );
}

function ColumnConfigurator({
  pinnedColumn,
  setPinnedColumn,
  table,
}: {
  pinnedColumn?: string;
  setPinnedColumn?: (value: string) => void;
  table: any;
}) {
  const columns = table.getAllLeafColumns();
  const [columnQuery, setColumnQuery] = useState<string>('');

  const filteredColumns = useMemo(
    () =>
      (columns || []).filter((col) => {
        const headerText = col.columnDef?.header;
        if (!columnQuery) return true;
        return (
          typeof headerText === 'string' &&
          headerText.toLowerCase().includes(columnQuery.toLowerCase())
        );
      }),
    [columnQuery, columns],
  );

  return (
    <div className={styles.columnConfigurator}>
      <div className={styles.columnConfigurator__header}>
        <Text>{locale(Keys.MANAGE_COLUMNS)}</Text>
        <Text classNames={styles.columnConfigurator__header__desc}>
          {locale(Keys.MANAGE_COLUMNS_DESC)}
        </Text>
      </div>

      <Divider />

      <div className={styles.columnConfigurator__search}>
        <Input
          placeholder="Search"
          onChange={(val) => setColumnQuery(val)}
          iconLeft={<SearchIcon />}
          classNames={styles.columnConfigurator__search__input}
        />
      </div>

      <Divider />

      <div className={styles.columnConfigurator__input}>
        <Radio
          type="radio"
          orientation="vertical"
          value={pinnedColumn}
          onChange={(value) => setPinnedColumn?.(value?.[0]?.code || '')}
          classNames={styles.columnConfigurator__input__pinRadios}
        >
          <RadioOption label="Pin First Column" value="left" />
          <RadioOption label="Pin Last Column" value="right" />
        </Radio>
      </div>

      <Divider />

      <DndContext
        onDragEnd={(event: DragEndEvent) => {
          const { active, over } = event;
          if (!over || active.id === over.id) return;

          const { columnOrder } = table.getState();
          const oldIndex = columnOrder.indexOf(active.id);
          const newIndex = columnOrder.indexOf(over.id);

          table.setColumnOrder(arrayMove(columnOrder, oldIndex, newIndex));
        }}
      >
        <div
          className={classnames(
            styles.columnConfigurator__input,
            styles.columnConfigurator__input__checkbox,
          )}
        >
          {filteredColumns
            .filter((col) => col.id !== 'select')
            .map((column) => (
              <div key={column.id}>
                <DraggableColumnRow column={column} />
              </div>
            ))}
        </div>
      </DndContext>

      <div className={styles.columnConfigurator__actions}>
        <Button
          type="text"
          onClick={() => {
            setPinnedColumn?.('');
            table.resetColumnVisibility?.(true);
          }}
          iconProps={{
            iconPosition: 'left',
            icon: <ResetIcon />,
          }}
        >
          {locale(Keys.RESET_ALL)}
        </Button>
      </div>
    </div>
  );
}

export default ColumnConfigurator;
